import { assertGraphCapture } from '@/simulation/native/authority';
import { LogicNodeInstance } from './LogicNodeInstance';
import { logicNodeRegistry } from './LogicNodeRegistry';
import { ILogicGraph } from '@/interfaces/models';
import { LogicEdge, LogicGraphDto } from '@/schemas/logic';
import { deepClone } from '@/utils/clone';
import type { NodeHandle, EdgeHandle } from './RuntimeHandles';
import {
  standaloneRuntimeIds,
  type RuntimeGraphIds,
  type RuntimeNodeImport,
} from './RuntimeGraphIds';
import { RuntimeBindings } from './RuntimeBindings';
import { nodeSnapshotWallAt, withNodeSnapshotTime } from './SavedNodeState';

/**
 * Logic graph data structure. NOT MobX-observable — graphs are rebuilt
 * frequently and the MobX proxy overhead on Map operations (set/get/delete)
 * dominated rebuild time for large circuits. Reactivity is handled at the
 * reference level: LogicCircuitModel.logicGraph is observable and swapped
 * on each rebuild.
 */
export interface SimulationNode {
  id: NodeHandle;
  slot: number;
  node: LogicNodeInstance | undefined;
  // Tracked graphs retain input/target references in graph-owned arenas.
  // Untracked lookups keep their own inputs for the captured simulation wave.
  inputs?: LogicNodeInstance[];
  inputStart: number;
  inputEnd: number;
  // Native caches resolve the index directly; extension lookups retain their list.
  outputs?: LogicEdge[];
  targets?: SimulationNode[];
  targetStart: number;
  targetEnd: number;
  timingOwner?: object;
  timing?: { last: number };
}

type EdgeIndex = Map<NodeHandle, LogicEdge | LogicEdge[] | undefined>;

export class LogicGraph implements ILogicGraph {
  #editingOwnership = undefined;
  static supportsEditingOwnership(graph: ILogicGraph): boolean {
    if (
      !(#editingOwnership in graph) ||
      Object.getPrototypeOf(graph) !== LogicGraph.prototype
    )
      return false;
    if (
      Object.hasOwn(graph, 'nodes') ||
      Object.hasOwn(graph, 'constructor') ||
      Object.getOwnPropertyDescriptor(graph, 'isTopologyTracked')?.value !== true
    )
      return false;
    const edges = Object.getOwnPropertyDescriptor(graph, 'edges');
    if (!edges || !('value' in edges)) return false;
    try {
      nativeEditingMapSize.call(edges.value);
      if (
        Object.getPrototypeOf(edges.value) !== Map.prototype ||
        Object.hasOwn(edges.value, 'values') ||
        Object.hasOwn(edges.value, Symbol.iterator) ||
        Object.getOwnPropertyDescriptor(Map.prototype, 'values')?.value !==
          nativeEditingMapValues
      )
        return false;
    } catch {
      return false;
    }
    for (const [name, native] of nativeEditingDescriptors) {
      const descriptor =
        Object.getOwnPropertyDescriptor(graph, name) ??
        Object.getOwnPropertyDescriptor(LogicGraph.prototype, name);
      if (
        !descriptor ||
        descriptor.value !== native.value ||
        descriptor.get !== native.get ||
        descriptor.set !== native.set
      )
        return false;
    }
    return true;
  }
  constructor(readonly ids: RuntimeGraphIds = standaloneRuntimeIds) {}
  private mayHaveImportedIdentities = false;
  private importedMissingReferences = new Map<NodeHandle, string>();
  private nextSimulationSlot = 0;
  topologyRevision = 0;
  isTopologyTracked = true;
  private simulationNodes = new Map<NodeHandle, SimulationNode>();
  private simulationInputs: LogicNodeInstance[] = [];
  private simulationTargets: SimulationNode[] = [];

  private invalidateSimulationNodes() {
    this.simulationNodes.clear();
    if (this.simulationInputs.length) this.simulationInputs = [];
    if (this.simulationTargets.length) this.simulationTargets = [];
    this.nextSimulationSlot = 0;
  }
  private nodeMap: Map<NodeHandle, LogicNodeInstance> = new TopologyNodeMap(
    () => {
      this.topologyRevision++;
      this.invalidateSimulationNodes();
    },
    (id, node) => {
      this.ids.assertSame(node.ids);
      if (id !== node.id) throw new TypeError('Node handle does not match graph key');
      if (node.hasImportedIdentity) this.mayHaveImportedIdentities = true;
    },
  );
  get nodes(): Map<NodeHandle, LogicNodeInstance> {
    return this.nodeMap;
  }
  set nodes(nodes: Map<NodeHandle, LogicNodeInstance>) {
    if (nodes === this.nodeMap) return;
    // Preserve aliases to externally supplied maps. Their mutations cannot be
    // tracked, so simulation falls back to fresh lookups for these graphs.
    for (const [id, node] of nodes) {
      this.ids.assertSame(node.ids);
      if (id !== node.id) throw new TypeError('Node handle does not match graph key');
    }
    this.mayHaveImportedIdentities = true; // External mutations require conservative boundary checks.
    this.nodeMap = nodes;
    this.isTopologyTracked = false;
    this.topologyRevision++;
    this.invalidateSimulationNodes();
  }

  peekSimulationNode(id: NodeHandle): SimulationNode | undefined {
    return this.simulationNodes.get(id);
  }

  /** Resolve hot-loop references once; every topology mutation invalidates them. */
  getSimulationNode(id: NodeHandle): SimulationNode {
    let entry = this.simulationNodes.get(id);
    if (entry) return entry;
    const node = this.nodes.get(id);
    const cached = node && this.isTopologyTracked;
    const arena =
      cached &&
      Object.getPrototypeOf(this) === LogicGraph.prototype &&
      this.getSimulationNode === nativeSimulationNode &&
      this.getEdgesTo === nativeEdgesTo &&
      this.getEdgesFrom === nativeEdgesFrom;
    const inputs = arena ? this.simulationInputs : [];
    const inputStart = inputs.length;
    for (const edge of arena
      ? this.indexedEdges(this.edgesByTarget, id)
      : this.getEdgesTo(id)) {
      const input = this.nodes.get(edge.sourceNodeId);
      if (input) inputs.push(input);
    }
    entry = {
      id,
      slot: cached ? this.nextSimulationSlot++ : -1,
      node,
      inputs: arena ? undefined : inputs,
      inputStart,
      inputEnd: inputs.length,
      outputs: arena ? undefined : this.getEdgesFrom(id),
      targetStart: -1,
      targetEnd: -1,
      timingOwner: undefined,
      timing: undefined,
    };
    if (cached) this.simulationNodes.set(id, entry);
    return entry;
  }

  getSimulationInput(entry: SimulationNode): boolean {
    const inputs = entry.inputs ?? this.simulationInputs;
    for (let index = entry.inputStart; index < entry.inputEnd; index++)
      if (inputs[index].output) return true;
    return false;
  }

  /** Only used in a wave whose graph and topology revision remain current. */
  getSimulationTargets(entry: SimulationNode): readonly SimulationNode[] {
    if (entry.inputs) {
      // Subclasses and replaced lookup hooks keep the original independent
      // cache containers; their lookups may run arbitrary extension code.
      entry.targets ??= entry.outputs!.map((edge) =>
        this.getSimulationNode(edge.targetNodeId),
      );
      entry.targetStart = 0;
      entry.targetEnd = entry.targets.length;
      return entry.targets;
    }
    if (entry.targetStart < 0) {
      // Resolving a target can append input references, but cannot publish any
      // target ranges. Reserve this range after all target lookups complete.
      // Array.map captures the original length even if a replaced lookup hook
      // appends edges while resolving targets.
      const outputs = entry.outputs ?? this.edgesBySource.get(entry.id);
      const targets = Array.isArray(outputs)
        ? outputs.map((edge) => this.getSimulationNode(edge.targetNodeId))
        : outputs
          ? [this.getSimulationNode(outputs.targetNodeId)]
          : [];
      entry.targetStart = this.simulationTargets.length;
      for (const target of targets) this.simulationTargets.push(target);
      entry.targetEnd = this.simulationTargets.length;
    }
    return this.simulationTargets;
  }
  edges = new Map<EdgeHandle, LogicEdge>();

  // Optimization: Index edges by target node ID for fast lookup
  private edgesByTarget = new Map<NodeHandle, LogicEdge | LogicEdge[] | undefined>();
  private edgesBySource = new Map<NodeHandle, LogicEdge | LogicEdge[] | undefined>();

  static fromDto(
    dto: LogicGraphDto,
    ids: RuntimeGraphIds = standaloneRuntimeIds,
    importer: RuntimeNodeImport = ids.importer(),
  ): LogicGraph {
    ids.assertSame(importer.ids);
    const graph = new LogicGraph(ids);
    for (const nodeDto of dto.nodes) {
      const node = LogicNodeInstance.fromDto(nodeDto, importer);
      if (graph.nodes.has(node.id))
        throw new TypeError('Duplicate imported node identity');
      graph.nodes.set(node.id, node);
    }

    for (const edgeDto of dto.edges) {
      const edge: LogicEdge = {
        ...deepClone(edgeDto),
        id: ids.scope.allocateEdge(),
        sourceNodeId: importer.node(edgeDto.sourceNodeId),
        targetNodeId: importer.node(edgeDto.targetNodeId),
        externalId: edgeDto.id,
      };
      if (!graph.nodes.has(edge.sourceNodeId))
        graph.importedMissingReferences.set(edge.sourceNodeId, edgeDto.sourceNodeId);
      if (!graph.nodes.has(edge.targetNodeId))
        graph.importedMissingReferences.set(edge.targetNodeId, edgeDto.targetNodeId);
      graph.edges.set(edge.id, edge);

      graph.indexEdge(edge);
    }

    return graph;
  }

  get dto(): LogicGraphDto {
    assertGraphCapture(this);
    return withNodeSnapshotTime(nodeSnapshotWallAt(), () => ({
      nodes: [...this.boundaryNodes()].map((node) => node.dto),
      edges: [...this.edges.values()].map((edge) => this.edgeDto(edge)),
    }));
  }

  /** Check persisted spellings only at the selected owner boundary, never on hot graph merges. */
  *boundaryNodes(selected?: readonly NodeHandle[]): Iterable<LogicNodeInstance> {
    const seen = this.mayHaveImportedIdentities ? new Set<string>() : undefined;
    for (const id of selected ?? this.nodes.keys()) {
      const node = this.nodes.get(id);
      if (!node) throw new Error('Graph changed during coherent capture');
      if (!this.isTopologyTracked) {
        this.ids.assertSame(node.ids);
        if (id !== node.id) throw new TypeError('Node handle does not match graph key');
      }
      if (seen) {
        if (seen.has(node.idText))
          throw new TypeError('Imported node identities collide in project export');
        seen.add(node.idText);
      }
      yield node;
    }
  }

  nodeIdText(id: NodeHandle): string {
    return (
      this.nodes.get(id)?.idText ??
      this.importedMissingReferences.get(id) ??
      this.ids.nodeText(id)
    );
  }

  edgeDto(edge: LogicEdge): import('@/schemas/logic').LogicEdgeDto {
    const { externalId, ...value } = edge;
    return {
      ...deepClone(value),
      id: externalId ?? this.ids.edgeText(edge.id),
      sourceNodeId: this.nodeIdText(edge.sourceNodeId),
      targetNodeId: this.nodeIdText(edge.targetNodeId),
    };
  }

  addNode(type: string): LogicNodeInstance {
    const id = this.ids.scope.allocateNode();
    const definition = logicNodeRegistry.get(type);
    const node = new LogicNodeInstance(id, definition, this.ids);
    this.nodes.set(id, node);
    return node;
  }

  removeNode(id: NodeHandle) {
    this.nodes.delete(id);
    // Use edge indexes for O(k) removal instead of iterating all edges
    const sourceEdges = [...this.indexedEdges(this.edgesBySource, id)];
    const targetEdges = [...this.indexedEdges(this.edgesByTarget, id)];
    for (const edge of sourceEdges) {
      this.removeEdge(edge.id);
    }
    for (const edge of targetEdges) {
      this.removeEdge(edge.id);
    }
  }

  addEdge(
    sourceNodeId: NodeHandle,
    targetNodeId: NodeHandle,
    meta?: LogicEdge['meta'],
  ): LogicEdge {
    // Check for existing edge
    const existingEdges = this.edgesByTarget.get(targetNodeId);
    if (existingEdges) {
      const existing = Array.isArray(existingEdges)
        ? existingEdges.find((e) => e.sourceNodeId === sourceNodeId)
        : existingEdges.sourceNodeId === sourceNodeId
          ? existingEdges
          : undefined;
      if (existing) return existing;
    }

    this.topologyRevision++;
    this.invalidateSimulationNodes();
    const id = this.ids.scope.allocateEdge();
    const edge: LogicEdge = { id, sourceNodeId, targetNodeId, meta };
    this.edges.set(id, edge);

    this.indexEdge(edge);

    return edge;
  }

  private indexEdge(edge: LogicEdge) {
    this.appendIndexedEdge(this.edgesByTarget, edge.targetNodeId, edge);
    this.appendIndexedEdge(this.edgesBySource, edge.sourceNodeId, edge);
  }

  // Most node directions have one edge. Materialize lists only for fan-in/out
  // or public lookups; existing exposed lists continue mutating in place.
  private appendIndexedEdge(index: EdgeIndex, id: NodeHandle, edge: LogicEdge) {
    const current = index.get(id);
    if (!current) index.set(id, edge);
    else if (Array.isArray(current)) current.push(edge);
    else index.set(id, [current, edge]);
  }

  private *indexedEdges(index: EdgeIndex, id: NodeHandle): Iterable<LogicEdge> {
    const current = index.get(id);
    if (Array.isArray(current)) yield* current;
    else if (current) yield current;
  }

  private exposedEdges(index: EdgeIndex, id: NodeHandle): LogicEdge[] {
    const current = index.get(id);
    if (!current) {
      const edges: LogicEdge[] = [];
      // A removed singleton preserves its historical empty-list identity once
      // exposed. Never-connected lookups keep returning independent empty lists.
      if (index.has(id)) index.set(id, edges);
      return edges;
    }
    if (Array.isArray(current)) return current;
    const edges = [current];
    index.set(id, edges);
    return edges;
  }

  private removeIndexedEdge(index: EdgeIndex, nodeId: NodeHandle, edgeId: EdgeHandle) {
    const current = index.get(nodeId);
    if (Array.isArray(current)) {
      const position = current.findIndex((edge) => edge.id === edgeId);
      if (position !== -1) current.splice(position, 1);
    } else if (current?.id === edgeId) {
      // Keep historical membership without allocating an unused empty list.
      index.set(nodeId, undefined);
    }
  }

  removeEdge(id: EdgeHandle) {
    const edge = this.edges.get(id);
    if (edge) {
      this.topologyRevision++;
      this.invalidateSimulationNodes();
      this.edges.delete(id);
      this.removeIndexedEdge(this.edgesByTarget, edge.targetNodeId, id);
      this.removeIndexedEdge(this.edgesBySource, edge.sourceNodeId, id);
    }
  }

  getEdgesFrom(nodeId: NodeHandle): LogicEdge[] {
    return this.exposedEdges(this.edgesBySource, nodeId);
  }

  getEdgesTo(nodeId: NodeHandle): LogicEdge[] {
    return this.exposedEdges(this.edgesByTarget, nodeId);
  }

  getNodeInput(nodeId: NodeHandle): boolean {
    const incomingEdges = this.edgesByTarget.get(nodeId);
    if (!incomingEdges) return false;

    for (const edge of this.indexedEdges(this.edgesByTarget, nodeId)) {
      const sourceNode = this.nodes.get(edge.sourceNodeId);
      if (sourceNode && sourceNode.output) {
        return true; // OR logic
      }
    }
    return false;
  }

  /**
   * Create a deep clone of this graph with new node IDs.
   * Returns the cloned graph and a mapping from old node IDs to new node IDs.
   */
  clone(): { graph: LogicGraph; idMap: RuntimeBindings } {
    const cloned = new LogicGraph(this.ids);
    const idMap = new RuntimeBindings();

    // Clone all nodes with new IDs
    for (const [oldId, node] of this.nodes) {
      const newId = this.ids.scope.allocateNode();
      idMap.set(oldId, newId);
      const clonedNode = node.clone(newId);
      cloned.nodes.set(newId, clonedNode);
    }

    // Remap and clone all edges
    for (const edge of this.edges.values()) {
      const newSourceId = idMap.get(edge.sourceNodeId);
      const newTargetId = idMap.get(edge.targetNodeId);
      if (newSourceId && newTargetId) {
        cloned.addEdge(
          newSourceId,
          newTargetId,
          edge.meta ? deepClone(edge.meta) : undefined,
        );
      }
    }

    return { graph: cloned, idMap };
  }

  /**
   * Merge all nodes and edges from another graph into this graph.
   * Node instances are moved directly (not cloned) so their state is preserved.
   * Edges are re-created via addEdge to maintain internal indexes.
   */
  mergeFrom(other: ILogicGraph): void {
    this.ids.assertSame(other.ids);
    if (other instanceof LogicGraph)
      for (const [id, spelling] of other.importedMissingReferences)
        this.importedMissingReferences.set(id, spelling);
    for (const [id, node] of other.nodes) {
      this.nodes.set(id, node);
    }
    for (const edge of other.edges.values()) {
      this.addEdge(edge.sourceNodeId, edge.targetNodeId, edge.meta);
    }
  }
}

const nativeEditingDescriptors = [
  'nodes',
  'dto',
  'boundaryNodes',
  'edgeDto',
  'nodeIdText',
].map(
  (name) => [name, Object.getOwnPropertyDescriptor(LogicGraph.prototype, name)!] as const,
);
const nativeEditingMapSize = Object.getOwnPropertyDescriptor(Map.prototype, 'size')!.get!;
const nativeEditingMapValues = Map.prototype.values;

const nativeSimulationNode = LogicGraph.prototype.getSimulationNode;
const nativeEdgesTo = LogicGraph.prototype.getEdgesTo;
const nativeEdgesFrom = LogicGraph.prototype.getEdgesFrom;

/** Direct node-map mutations must invalidate resolved input references too. */
class TopologyNodeMap extends Map<NodeHandle, LogicNodeInstance> {
  constructor(
    private changed: () => void,
    private validate: (id: NodeHandle, node: LogicNodeInstance) => void,
    entries?: Iterable<[NodeHandle, LogicNodeInstance]>,
  ) {
    super();
    if (entries) for (const [id, node] of entries) super.set(id, node);
  }
  override set(id: NodeHandle, node: LogicNodeInstance): this {
    this.validate(id, node);
    if (this.get(id) !== node) this.changed();
    return super.set(id, node);
  }
  override delete(id: NodeHandle): boolean {
    const removed = super.delete(id);
    if (removed) this.changed();
    return removed;
  }
  override clear(): void {
    if (this.size) this.changed();
    super.clear();
  }
}
